import { api } from "../api.ts";
import { formatDuration, h, icon } from "../dom.ts";
import { navigate } from "../router.ts";

export async function renderHome(root: HTMLElement): Promise<void> {
  const programme = await api.programme();
  document.title = `${programme.title} · ${programme.subtitle}`;
  const total = programme.modules.reduce((sum, m) => sum + m.duration, 0);

  const cards = programme.modules.map((m) => {
    const done = programme.progress[m.id]?.length ?? 0;
    const count = m.steps.length;
    const ratio = count ? done / count : 0;
    const state = !m.available ? "soon" : ratio === 1 ? "done" : ratio > 0 ? "started" : "todo";
    const label = { soon: "En préparation", done: "Terminé", started: "En cours", todo: "Commencer" }[state];

    const content = [
      h("span", { class: "module-num" }, String(m.order)),
      h(
        "div",
        { class: "module-body" },
        h("h3", {}, m.title),
        h("p", {}, m.summary),
        h(
          "div",
          { class: "module-meta" },
          h("span", {}, icon("clock", 14), formatDuration(m.duration)),
          m.available ? h("span", {}, `${count} étapes`) : null,
          h("span", { class: `badge badge-${state}` }, state === "soon" ? icon("lock", 12) : null, label),
        ),
        m.available && ratio > 0
          ? h("div", { class: "progress", role: "progressbar", "aria-valuenow": Math.round(ratio * 100), "aria-valuemin": 0, "aria-valuemax": 100 }, h("span", { style: `width:${ratio * 100}%` }))
          : null,
      ),
    ];

    return m.available
      ? h(
          "a",
          {
            class: `module-card is-${state}`,
            href: `/modules/${m.id}`,
            onclick: (e: Event) => {
              e.preventDefault();
              navigate(`/modules/${m.id}`);
            },
          },
          content,
        )
      : h("div", { class: "module-card is-soon", "aria-disabled": "true" }, content);
  });

  root.replaceChildren(
    h(
      "div",
      { class: "home" },
      h(
        "header",
        { class: "hero" },
        h("div", { class: "brand brand-lg" }, h("span", { class: "brand-mark" }, icon("terminal", 22)), programme.title),
        h("h1", {}, programme.subtitle),
        h("p", { class: "lead" }, programme.description),
        h(
          "ul",
          { class: "hero-facts" },
          h("li", {}, icon("clock", 16), `${formatDuration(total)} de formation`),
          h("li", {}, icon("terminal", 16), "Un vrai Linux dans le navigateur"),
          h("li", {}, icon("check", 16), "Exercices vérifiés automatiquement"),
        ),
      ),
      h(
        "section",
        { class: "how" },
        h("h2", {}, "Comment ça marche"),
        h(
          "ol",
          { class: "how-steps" },
          h("li", {}, h("strong", {}, icon("book", 18), "Une notion à la fois"), h("span", {}, "Des explications courtes, centrées sur l'essentiel.")),
          h("li", {}, h("strong", {}, icon("flask", 18), "Mise en pratique immédiate"), h("span", {}, "Un environnement Linux personnel, prêt à l'emploi, à côté du cours.")),
          h("li", {}, h("strong", {}, icon("bulb", 18), "Jamais bloqué"), h("span", {}, "Vérification instantanée, indices progressifs, aide-mémoire et carnet de notes.")),
        ),
      ),
      h("section", { class: "programme" }, h("h2", {}, "Programme"), h("div", { class: "module-grid" }, cards)),
      h("footer", { class: "site-footer muted small" }, "Linux 101 · Les environnements sont réinitialisés après une période d'inactivité. Votre progression et vos notes sont conservées dans ce navigateur."),
    ),
  );
}
